import React from "react";
import {
  ExternalLink,
  BookOpen,
  FlaskConical,
  ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import KeyTakeaways from "@/components/sections/KeyTakeaways";
import ClinicalReviewNote from "@/components/ClinicalReviewNote";
import { RelatedPages } from "@/components/RelatedPages";
import {
  buildBreadcrumbList,
  CLINIC_REF,
  SITE_URL,
} from "@/components/schemaConstants";

const crumbs = [{ name: "Clinical Research", href: "/clinical-research" }];

/* ------------------------------------------------------------------ */
/* Study data — every entry is a real, verifiable publication.         */
/* No invented statistics, authors, or journals.                       */
/* ------------------------------------------------------------------ */

interface Study {
  title: string;
  authors: string;
  journal: string;
  year: string;
  population: string;
  design: string;
  finding: string;
  limitations: string;
  url: string;
}

const saintStudies: Study[] = [
  {
    title:
      "Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial",
    authors: "Cole EJ, Phillips AL, Bentzley BS, et al.",
    journal: "American Journal of Psychiatry",
    year: "2022",
    population:
      "29 adults with treatment-resistant depression (analyzed; 32 enrolled)",
    design: "Double-blind, sham-controlled randomized controlled trial",
    finding:
      "78.6% of the active treatment group met remission criteria at 4 weeks, compared to 13.3% of the sham group.",
    limitations:
      "Small sample from a single site; follow-up limited to 4 weeks; no head-to-head comparison with standard TMS.",
    url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
  },
  {
    title:
      "Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression",
    authors: "Cole EJ, Stimpson KH, Bentzley BS, et al.",
    journal: "American Journal of Psychiatry",
    year: "2020",
    population: "21 adults with treatment-resistant depression",
    design: "Open-label (no sham control)",
    finding:
      "19 of 21 completers (90.5%) met remission criteria after 5 days of treatment.",
    limitations:
      "No sham comparison group; small sample; single site; open-label design can introduce expectancy bias.",
    url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
  },
];

const tmsStudies: Study[] = [
  {
    title:
      "Efficacy and Safety of Transcranial Magnetic Stimulation in the Acute Treatment of Major Depression",
    authors: "O'Reardon JP, Solvason HB, Janicak PG, et al.",
    journal: "Biological Psychiatry",
    year: "2007",
    population: "301 adults with major depressive disorder",
    design: "Multisite randomized sham-controlled trial",
    finding:
      "Active TMS was superior to sham, with a statistically significant improvement in depression scores.",
    limitations:
      "Short-term acute treatment phase; response and remission rates were modest; long-term durability not established in this trial.",
    url: "https://pubmed.ncbi.nlm.nih.gov/17192865/",
  },
  {
    title:
      "Transcranial Magnetic Stimulation (TMS) for Major Depression: A Multisite, Naturalistic, Observational Study",
    authors: "Carpenter LL, Janicak PG, Aaronson ST, et al.",
    journal: "Depression and Anxiety",
    year: "2012",
    population: "307 patients in real-world clinical practice",
    design: "Naturalistic observational study (no control group)",
    finding:
      "Approximately 58% response and 37% remission on clinician-rated scales among patients completing treatment.",
    limitations:
      "No control group; open-label; reflects patients who completed treatment, not all who started; clinician-rated outcomes may differ from patient-rated.",
    url: "https://pubmed.ncbi.nlm.nih.gov/22225677/",
  },
  {
    title:
      "Effectiveness of Theta Burst Versus High-Frequency Repetitive Transcranial Magnetic Stimulation in Patients with Depression (THREE-D)",
    authors: "Blumberger DM, Vila-Rodriguez F, Thorpe KE, et al.",
    journal: "Lancet",
    year: "2018",
    population: "414 adults with treatment-resistant depression",
    design: "Randomized non-inferiority trial (two active arms, no sham)",
    finding:
      "Intermittent theta burst stimulation (iTBS) was non-inferior to 10 Hz rTMS, with similar symptom improvement and side effects.",
    limitations:
      "Compared two active treatments without a sham arm; results apply to once-daily treatment, not accelerated schedules.",
    url: "https://pubmed.ncbi.nlm.nih.gov/29519681/",
  },
];

const spravatoStudies: Study[] = [
  {
    title:
      "Efficacy and Safety of Fixed-Dose Esketamine Nasal Spray Combined with a New Oral Antidepressant in Treatment-Resistant Depression",
    authors: "Daly EJ, Singh JB, Fedgchin M, et al.",
    journal: "JAMA Psychiatry",
    year: "2018",
    population:
      "Adults with treatment-resistant depression who had not responded to 2–5 antidepressant trials",
    design: "Phase 3 randomized double-blind sham-controlled trial",
    finding:
      "Esketamine plus a new oral antidepressant showed greater symptom improvement than the nasal sham plus a new oral antidepressant at day 28.",
    limitations:
      "Modest effect size; dissociation and other adverse effects were common; long-term safety data came from open-label extension studies.",
    url: "https://pubmed.ncbi.nlm.nih.gov/29466512/",
  },
  {
    title:
      "SPRAVATO® (esketamine) Prescribing Information — FDA-Approved Labeling",
    authors: "Janssen Pharmaceuticals, Inc. / U.S. FDA",
    journal: "FDA Drug Labeling (BLA 211243)",
    year: "2019 (updated 2025)",
    population:
      "Adults with treatment-resistant depression; adults with MDD and acute suicidal ideation/behavior",
    design: "FDA regulatory review of pivotal trials",
    finding:
      "FDA-approved for treatment-resistant depression (as monotherapy or with an oral antidepressant) and for depressive symptoms in adults with acute suicidal ideation/behavior (with an oral antidepressant). Administered under a REMS program.",
    limitations:
      "Approval is specific to these indications; other uses are off-label. Common effects include dissociation, sedation, and blood pressure elevation.",
    url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const StudyCard: React.FC<{ study: Study }> = ({ study }) => (
  <article className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
    <h3 className="font-headings text-base font-bold text-primary leading-snug">
      {study.title}
    </h3>
    <p className="mt-2 text-sm text-muted-foreground">
      {study.authors} · <span className="italic">{study.journal}</span>,{" "}
      {study.year}
    </p>
    <dl className="mt-4 space-y-2 text-sm">
      <div>
        <dt className="inline font-semibold text-primary">Population: </dt>
        <dd className="inline text-foreground/85">{study.population}</dd>
      </div>
      <div>
        <dt className="inline font-semibold text-primary">Study design: </dt>
        <dd className="inline text-foreground/85">{study.design}</dd>
      </div>
      <div>
        <dt className="inline font-semibold text-primary">Main finding: </dt>
        <dd className="inline text-foreground/85">{study.finding}</dd>
      </div>
      <div>
        <dt className="inline font-semibold text-primary">Limitations: </dt>
        <dd className="inline text-foreground/85">{study.limitations}</dd>
      </div>
    </dl>
    <a
      href={study.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline underline-offset-2"
    >
      View original source
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  </article>
);

const ClinicalResearchPage: React.FC = () => {
  const faqs = [
    {
      q: "Where can I read the original SAINT clinical trials?",
      a: "The two primary SAINT publications are Cole et al. (2022) in the American Journal of Psychiatry — the double-blind sham-controlled trial — and Cole et al. (2020), the open-label study. Both are linked above with direct URLs to PubMed. We encourage patients and families to read the original research and discuss it with a qualified clinician.",
    },
    {
      q: "Does the 79% remission rate mean I will go into remission?",
      a: "No. The 79% figure comes from the active treatment group in a small, single-site randomized trial of 29 analyzed patients. It describes the group studied, not any individual's likelihood. Individual results vary, and no treatment for depression guarantees remission.",
    },
    {
      q: "Is the research on TMS for PTSD or bipolar depression as strong as for depression?",
      a: "No. TMS for major depressive disorder has multiple randomized sham-controlled trials and FDA clearance. TMS for PTSD and bipolar depression has smaller, mixed studies and no FDA-cleared device indication. Those uses are off-label.",
    },
    {
      q: "What is the difference between a sham-controlled trial and an open-label study?",
      a: "A sham-controlled trial includes a placebo (sham) group that receives a fake treatment, so researchers can isolate whether the real treatment itself is causing improvement. An open-label study has no placebo group — everyone receives the real treatment — so expectancy and placebo effects cannot be separated from the treatment's effect.",
    },
    {
      q: "Does Neuro Wellness conduct clinical trials?",
      a: "No. Neuro Wellness TMS Centers of America is a treatment provider, not a research institution. We offer treatments within their FDA-authorized indications. If you are interested in participating in research, search ClinicalTrials.gov or ask your clinician about ongoing trials at academic medical centers.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Clinical Research Library | Neuro Wellness TMS"
        description="Peer-reviewed research behind SAINT, TMS, and SPRAVATO: study designs, findings, limitations, and links to original sources. Evidence-based, not marketing."
        canonical="/clinical-research"
        keywords="SAINT clinical trial, TMS research, esketamine studies, Stanford neuromodulation research, treatment-resistant depression evidence, Cole et al 2022"
        schemaJson={[
          buildBreadcrumbList([{ name: "Home", href: "/" }, ...crumbs]),
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Clinical Research Behind Advanced Depression Treatments",
            url: `${SITE_URL}/clinical-research`,
            inLanguage: "en-US",
            about: [
              { "@type": "MedicalTherapy", name: "SAINT® Accelerated TMS" },
              {
                "@type": "MedicalTherapy",
                name: "Repetitive Transcranial Magnetic Stimulation (rTMS)",
              },
              {
                "@type": "MedicalTherapy",
                name: "SPRAVATO® (esketamine) treatment",
                description:
                  "FDA-approved esketamine nasal spray for treatment-resistant depression, administered under a REMS monitoring program.",
              },
            ],
            publisher: CLINIC_REF,
            mainEntity: {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-10 sm:py-14">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs crumbs={crumbs} className="mb-6" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-accent">
              <FlaskConical className="h-4 w-4" />
              Evidence Library
            </span>
            <h1 className="mt-3 font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl md:text-4xl">
              Clinical Research Behind Advanced Depression Treatments
            </h1>
            <p className="mt-4 max-w-3xl text-base text-foreground/80 leading-relaxed sm:text-lg">
              This library summarizes peer-reviewed and authoritative research
              related to treatments offered by Neuro Wellness TMS Centers of
              America, including SAINT® TMS, conventional TMS, and SPRAVATO®
              (esketamine). For every study we list the design, population, main
              finding, and important limitations, with a direct link to the
              original source. This is an educational resource, not medical
              advice.
            </p>
            <div className="mt-4">
              <ClinicalReviewNote
                reviewedBy="Daniel Pietras, MD — Board-Certified Psychiatrist"
                lastUpdated="2026-11-03"
              />
            </div>
          </div>
        </section>

        {/* Key takeaways */}
        <KeyTakeaways
          items={[
            {
              text: "SAINT®'s 79% remission figure comes from a double-blind, sham-controlled randomized trial of 29 analyzed patients (Cole et al., Am J Psychiatry, 2022). It is not a guarantee of individual outcome.",
            },
            {
              text: "Conventional TMS has the largest evidence base, including a 301-patient sham-controlled trial and a 307-patient real-world study reporting ~58% response and ~37% remission among completers.",
            },
            {
              text: "SPRAVATO® (esketamine) is FDA-approved for treatment-resistant depression based on Phase 3 sham-controlled trials; it is administered under a REMS monitoring program.",
            },
            {
              text: "Study results describe the populations studied. Different study designs (sham-controlled vs. open-label vs. registry) produce different types of evidence and are not directly comparable.",
            },
            {
              text: "Neuro Wellness TMS Centers of America offers treatments within their FDA-authorized indications. We do not conduct clinical trials.",
            },
          ]}
        />

        {/* SAINT Research */}
        <section className="py-10 sm:py-12" id="saint-research">
          <div className="container-px mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <BookOpen className="h-5 w-5" />
              </span>
              <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
                SAINT® Research
              </h2>
            </div>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy)
              was developed at Stanford University and is delivered on the
              Magnus Neuromodulation System. The FDA cleared the device under
              510(k) K220177 in September 2022 for adults with major depressive
              disorder who have not achieved satisfactory improvement from prior
              antidepressant medication in the current episode.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {saintStudies.map((s, i) => (
                <StudyCard key={i} study={s} />
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-accent/20 bg-accent/[0.04] p-4">
              <p className="text-sm text-foreground/85 leading-relaxed">
                <span className="font-semibold text-primary">
                  FDA clearance:{" "}
                </span>
                Magnus Neuromodulation System with SAINT Technology, 510(k)
                K220177, cleared September 2022. FDA Breakthrough Device
                Designation.{" "}
                <a
                  href="https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-accent hover:underline underline-offset-2"
                >
                  View FDA 510(k) record
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Conventional TMS Research */}
        <section
          className="py-10 sm:py-12 bg-muted/20 border-y border-border"
          id="tms-research"
        >
          <div className="container-px mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <BookOpen className="h-5 w-5" />
              </span>
              <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
                Conventional TMS Research
              </h2>
            </div>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              Repetitive transcranial magnetic stimulation (rTMS) has the
              largest evidence base among the treatments offered at Neuro
              Wellness. The first TMS device was FDA-cleared for depression in
              2008. The studies below represent the pivotal sham-controlled
              trial, a major real-world registry, and the THREE-D trial that
              established theta burst stimulation as non-inferior to standard 10
              Hz TMS.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {tmsStudies.map((s, i) => (
                <StudyCard key={i} study={s} />
              ))}
            </div>
          </div>
        </section>

        {/* SPRAVATO Research */}
        <section className="py-10 sm:py-12" id="spravato-research">
          <div className="container-px mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <BookOpen className="h-5 w-5" />
              </span>
              <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
                SPRAVATO® (Esketamine) Research
              </h2>
            </div>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              SPRAVATO® (esketamine) nasal spray is FDA-approved for
              treatment-resistant depression and for depressive symptoms in
              adults with acute suicidal ideation or behavior. It is
              administered under a REMS (Risk Evaluation and Mitigation
              Strategy) program with mandatory in-office monitoring. The entries
              below link to the pivotal published trial and the FDA-approved
              prescribing information.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {spravatoStudies.map((s, i) => (
                <StudyCard key={i} study={s} />
              ))}
            </div>
          </div>
        </section>

        {/* Accelerated TMS — cleared vs investigational */}
        <section
          className="py-10 sm:py-12 bg-muted/20 border-y border-border"
          id="accelerated-tms-research"
        >
          <div className="container-px mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
                Accelerated TMS: FDA-Cleared vs. Investigational Protocols
              </h2>
            </div>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              "Accelerated TMS" is a broad term for any TMS schedule delivering
              more than one session per day. It is important to distinguish the
              FDA-cleared accelerated protocol from off-label or investigational
              schedules.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="py-3 pr-4 text-left font-bold text-primary">
                      Protocol
                    </th>
                    <th className="py-3 pr-4 text-left font-bold text-primary">
                      FDA Status
                    </th>
                    <th className="py-3 text-left font-bold text-primary">
                      Key Evidence
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      SAINT® (Magnus System)
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">
                      FDA-cleared (K220177)
                    </td>
                    <td className="py-3 text-foreground/85">
                      Double-blind sham-controlled RCT + open-label study (Cole
                      et al., 2020 &amp; 2022)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      Other accelerated schedules on conventional devices
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">Off-label</td>
                    <td className="py-3 text-foreground/85">
                      Varied small studies; no FDA-reviewed protocol; evidence
                      base is smaller and heterogeneous
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      Once-daily iTBS (theta burst)
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">
                      FDA-cleared (once-daily)
                    </td>
                    <td className="py-3 text-foreground/85">
                      THREE-D trial (Blumberger et al., 2018) — non-inferiority
                      to 10 Hz rTMS
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              For a full breakdown of FDA-cleared vs. investigational uses
              across all conditions, see our{" "}
              <a
                href="/research-and-investigational-treatments"
                className="font-semibold text-accent hover:underline underline-offset-2"
              >
                Research &amp; Investigational Treatments
              </a>{" "}
              page.
            </p>
          </div>
        </section>

        {/* How to read this research */}
        <section className="py-10 sm:py-12">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
              How to Read This Research
            </h2>
            <div className="mt-4 space-y-4 text-base text-foreground/85 leading-relaxed">
              <p>
                Clinical research uses different study designs, and each has
                different strengths. A double-blind, sham-controlled randomized
                trial is the strongest type of evidence because it isolates
                whether the treatment itself causes improvement. An open-label
                study, where everyone receives the real treatment, cannot
                separate the treatment's effect from placebo or expectancy.
              </p>
              <p>
                A registry or naturalistic study counts real-world patients who
                completed treatment, which reflects clinical practice but
                includes no comparison group. Response and remission figures
                from different study designs are not directly comparable.
              </p>
              <p>
                Every study has limitations — small samples, single-site
                recruitment, short follow-up, or the absence of a control group.
                We list the key limitations for each study so you can weigh the
                evidence honestly. If you have questions about what a study
                means for your situation, discuss it with a qualified clinician.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 sm:py-12 bg-muted/20 border-y border-border">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl mb-5">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-border bg-card p-5 shadow-soft"
                >
                  <summary className="flex cursor-pointer items-center justify-between font-headings text-base font-bold text-primary marker:content-['']">
                    {f.q}
                    <span
                      aria-hidden="true"
                      className="ml-3 shrink-0 text-accent transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-base text-foreground/85 leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Crisis notice */}
        <div className="container-px mx-auto max-w-4xl py-6">
          <p className="text-sm text-muted-foreground">
            If you are thinking about harming yourself, call or text{" "}
            <a
              href="tel:988"
              className="font-bold text-destructive underline underline-offset-2"
            >
              988
            </a>{" "}
            (Suicide &amp; Crisis Lifeline) in the U.S., or go to the nearest
            emergency room.
          </p>
        </div>

        <RelatedPages
          heading="Related Resources"
          links={[
            {
              title: "Research & Investigational Treatments",
              href: "/research-and-investigational-treatments",
              description:
                "FDA-cleared vs. off-label vs. investigational uses explained",
            },
            {
              title: "SAINT® TMS Treatment",
              href: "/saint",
              description: "FDA-cleared 5-day accelerated TMS in Coral Springs",
            },
            {
              title: "TMS Therapy",
              href: "/services/tms",
              description:
                "Conventional TMS for treatment-resistant depression",
            },
            {
              title: "SPRAVATO® Treatment",
              href: "/services/spravato",
              description: "FDA-approved esketamine for TRD",
            },
            {
              title: "Am I a Candidate?",
              href: "/am-i-a-candidate",
              description: "Eligibility screening — not a diagnosis",
            },
            {
              title: "Compare Treatments",
              href: "/compare-treatments",
              description: "Side-by-side comparison of treatment options",
            },
          ]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};

export default ClinicalResearchPage;
